import { type ProductSurface, surfaceEnabled } from "~/config/productSurfaces";

/**
 * Whether an in-app address leads to a page this deployment hides
 * (`~/config/productSurfaces`). One answer for every surface that sends
 * somebody somewhere: Quick Search's commands, its search results, its
 * recent items and its ID lookup, and the settings menu. A hidden page
 * answers not found when opened directly; this is what keeps anything from
 * offering the way there in the first place.
 *
 * Takes a real address (`/acme/prompts?handle=x`) or a route template
 * (`/[project]/prompts`); only the path's first two segments decide.
 *
 * Spec: specs/branding/product-surfaces.feature
 */
export function isDestinationHidden(address: string): boolean {
  const surface = surfaceForDestination(address);
  return surface !== undefined && !surfaceEnabled(surface);
}

/** The settings pages behind the `advancedSettings` switch. */
const ADVANCED_SETTINGS_PAGES = new Set([
  "security",
  "authentication",
  "license",
  "secrets",
  "data-retention",
  "email-suppressions",
  "annotation-scores",
  "topic-clustering",
]);

/** Project pages of the LLM Ops Test and Build group. */
const BUILD_AND_TEST_PAGES = new Set([
  "prompts",
  "agents",
  "workflows",
  "studio",
  "chat",
  "datasets",
  "experiments",
  "annotations",
  "automations",
  "triggers",
]);

/** Project pages of agent testing. */
const AGENT_TESTING_PAGES = new Set(["simulations", "agent-testing"]);

/** Top-level segments that are not a project slug. */
const TOP_LEVEL_SEGMENTS = new Set([
  "settings",
  "ops",
  "admin",
  "auth",
  "onboarding",
  "invite",
  "cli",
  "dev",
  "authorize",
  "unsubscribe",
  "gateway",
  "governance",
  "me",
  "share",
]);

function surfaceForDestination(address: string): ProductSurface | undefined {
  const path = address.split(/[?#]/, 1)[0] ?? "";
  const [first, second] = path.split("/").filter(Boolean);
  if (!first) return undefined;

  if (first === "gateway") return "gateway";
  if (first === "governance" || first === "me") return "governance";
  if (first === "share") return "publicShareLinks";
  if (first === "settings") {
    if (second === "integrations") return "githubIntegration";
    if (second && ADVANCED_SETTINGS_PAGES.has(second))
      return "advancedSettings";
    return undefined;
  }
  if (TOP_LEVEL_SEGMENTS.has(first) || !second) return undefined;

  if (BUILD_AND_TEST_PAGES.has(second)) return "buildAndTestPages";
  if (AGENT_TESTING_PAGES.has(second)) return "agentTesting";
  return undefined;
}
